/**
 * ONDA-CA1 — templates de e-mail e de notificação ainda sem API própria. Os
 * dados abaixo são mock local, só para dar paridade estrutural com o protótipo
 * (decisão registrada em docs/ALVOS.md, alvo ONDA-CA1).
 *
 * O monitoramento de e-mails (aba Monitoramento de /admin/emails) saiu daqui: lê
 * a API, ver `dados.ts`. Trocar estes por `chamarApi` quando a fonte do modelo
 * de e-mail for decidida.
 */

export interface TemplateEmail {
  id: string;
  nome: string;
  tipo: string;
  assunto: string;
  corpo: string;
  corTitulo: string;
  ativo: boolean;
  variaveisDisponiveis: string[];
  atualizadoEm: string;
}

function horasAtras(h: number): string {
  return new Date(Date.now() - h * 3_600_000).toISOString();
}

export const TEMPLATES_EMAIL_MOCK: TemplateEmail[] = [
  {
    id: 'documento_vencimento',
    nome: 'Vencimento de documento',
    tipo: 'documento_vencimento',
    assunto: 'Documento próximo do vencimento — {{nome_documento}}',
    corpo:
      'Olá {{nome_credenciado}}, o documento {{nome_documento}} vence em {{dias_para_vencer}} dias. Regularize no portal do credenciado.',
    corTitulo: '#1e3a5f',
    ativo: true,
    variaveisDisponiveis: ['nome_credenciado', 'nome_documento', 'dias_para_vencer'],
    atualizadoEm: horasAtras(72),
  },
  {
    id: 'documento_status',
    nome: 'Status de documento',
    tipo: 'documento_status',
    assunto: 'Atualização sobre {{nome_documento}}',
    corpo: 'O documento {{nome_documento}} teve o status alterado para {{novo_status}}.',
    corTitulo: '#1e3a5f',
    ativo: true,
    variaveisDisponiveis: ['nome_documento', 'novo_status'],
    atualizadoEm: horasAtras(240),
  },
  {
    id: 'multa_aplicada',
    nome: 'Multa aplicada',
    tipo: 'multa_aplicada',
    assunto: 'Multa aplicada — protocolo {{protocolo}}',
    corpo:
      'Uma multa foi aplicada ao protocolo {{protocolo}}, no valor de {{valor}}. Motivo: {{motivo}}.',
    corTitulo: '#7f1d1d',
    ativo: true,
    variaveisDisponiveis: ['protocolo', 'valor', 'motivo'],
    atualizadoEm: horasAtras(30),
  },
  {
    id: 'inscricao_aprovada',
    nome: 'Inscrição aprovada',
    tipo: 'inscricao_aprovada',
    assunto: 'Sua inscrição foi aprovada',
    corpo:
      'Parabéns, {{nome_candidato}}. Sua inscrição no chamamento {{nome_chamamento}} foi aprovada.',
    corTitulo: '#14532d',
    ativo: true,
    variaveisDisponiveis: ['nome_candidato', 'nome_chamamento'],
    atualizadoEm: horasAtras(500),
  },
  {
    id: 'inscricao_indeferida',
    nome: 'Inscrição indeferida',
    tipo: 'inscricao_indeferida',
    assunto: 'Sua inscrição não foi aprovada',
    corpo: 'Olá {{nome_candidato}}, sua inscrição não foi aprovada. Motivo: {{motivo}}.',
    corTitulo: '#7f1d1d',
    ativo: false,
    variaveisDisponiveis: ['nome_candidato', 'motivo'],
    atualizadoEm: horasAtras(500),
  },
  {
    id: 'os_status',
    nome: 'Status da ordem de serviço',
    tipo: 'os_status',
    assunto: 'Ordem de serviço {{numero_os}} — {{novo_status}}',
    corpo: 'A ordem de serviço {{numero_os}} mudou para o status {{novo_status}}.',
    corTitulo: '#1e3a5f',
    ativo: true,
    variaveisDisponiveis: ['numero_os', 'novo_status'],
    atualizadoEm: horasAtras(10),
  },
];

/**
 * Canais de notificacao. O seletor da tela (`CANAIS`, em
 * `NotificacoesTemplatesPage.tsx`) oferece cinco, os mesmos do array
 * `CANAIS` de `NotificacoesTemplates.tsx:28` no prototipo — sem WHATSAPP.
 *
 * O tipo inclui `WHATSAPP` mesmo assim porque um template da listagem
 * (COBRANCA_DRE_APONTAMENTO) traz esse canal — sem ele no tipo o template
 * nao teria onde entrar. Distribuicao: TODOS 10, EMAIL 8, IN_APP 7, PUSH 1,
 * SMS 1, WHATSAPP 1.
 *
 * `IN_APP`, com sublinhado, confere com o mesmo array do prototipo — "INAPP"
 * sem sublinhado que aparecia antes era de outra tela (canais_solicitados de
 * FechamentoMensalV2ListPage), nao desta.
 */
export type Canal = 'TODOS' | 'EMAIL' | 'SMS' | 'PUSH' | 'IN_APP' | 'WHATSAPP';

export interface TemplateNotificacao {
  id: string;
  evento: string;
  canal: Canal;
  titulo: string;
  mensagem: string;
  ativo: boolean;
  atualizadoEm: string;
}

/**
 * Os 28 templates do prototipo aprovado (`/admin/notificacoes/templates`),
 * com evento, canal, titulo, mensagem, situacao e data de atualizacao lidos
 * da referencia. Antes daqui havia quatro entradas inventadas
 * (`fiscalizacao.defesa_recebida`, `solicitacao.triagem_concluida`,
 * `os.substituicao_confirmada`, `medicao.glosa_aplicada`) que nao existiam na
 * referencia — nenhum evento em comum com ela.
 *
 * A data e fixa, nao um deslocamento sobre a hora atual: o prototipo mostra
 * uma data estavel por template, e data que muda a cada carga impede comparar
 * as duas telas.
 */
export const TEMPLATES_NOTIFICACAO_MOCK: TemplateNotificacao[] = [
  {
    id: 'apontamento-aprovado-sme-email',
    evento: 'APONTAMENTO_APROVADO_SME',
    canal: 'EMAIL',
    titulo: 'Apontamento {{mes_referencia}} aprovado pela SME',
    mensagem:
      'A SME aprovou o apontamento de {{mes_referencia}} da DRE {{dre_codigo}}. Encaminhado ao DTP para fechamento financeiro.',
    ativo: true,
    atualizadoEm: '2026-09-07',
  },
  {
    id: 'apontamento-aprovado-sme-inapp',
    evento: 'APONTAMENTO_APROVADO_SME',
    canal: 'IN_APP',
    titulo: 'Aprovado pela SME — {{dre_codigo}}',
    mensagem: 'Apontamento de {{mes_referencia}} segue para o DTP.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'apontamento-enviado-sme-email',
    evento: 'APONTAMENTO_ENVIADO_SME',
    canal: 'EMAIL',
    titulo: 'Apontamento {{mes_referencia}} enviado pela DRE {{dre_codigo}}',
    mensagem:
      'A DRE {{dre_codigo}} — {{dre_nome}} enviou o apontamento de {{mes_referencia}} para análise da SME. Total de OS: {{qtd_os}}.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'apontamento-enviado-sme-inapp',
    evento: 'APONTAMENTO_ENVIADO_SME',
    canal: 'IN_APP',
    titulo: 'Apontamento {{mes_referencia}} aguardando análise',
    mensagem: 'DRE {{dre_codigo}} enviou {{qtd_os}} OS para análise.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'cobranca-dre-apontamento-email',
    evento: 'COBRANCA_DRE_APONTAMENTO',
    canal: 'EMAIL',
    titulo: 'Pendência de envio — competência {competencia} (resposta até {prazo_resposta})',
    mensagem:
      'Prezada DRE {dre_codigo} - {dre_nome}, Identificamos pendência no envio dos apontamentos da competência {competencia} ({qtd_lotes_pendentes} lote(s) aguardando). {mensagem_custom} Prazo de resposta: até {prazo_resposta} ({sla_dias_uteis} dias úteis). SME — TEG.',
    ativo: true,
    atualizadoEm: '2026-05-11',
  },
  {
    id: 'cobranca-dre-apontamento-push',
    evento: 'COBRANCA_DRE_APONTAMENTO',
    canal: 'PUSH',
    titulo: 'Apontamentos pendentes',
    mensagem: 'DRE {dre_codigo}: regularize a competência {competencia} até {prazo_resposta}.',
    ativo: true,
    atualizadoEm: '2026-05-11',
  },
  {
    id: 'cobranca-dre-apontamento-sms',
    evento: 'COBRANCA_DRE_APONTAMENTO',
    canal: 'SMS',
    titulo: 'Cobrança SME',
    mensagem:
      'TEG/SME: DRE {dre_codigo} — apontamentos {competencia} pendentes. Resposta até {prazo_resposta}.',
    ativo: true,
    atualizadoEm: '2026-05-11',
  },
  {
    id: 'cobranca-dre-apontamento-whatsapp',
    evento: 'COBRANCA_DRE_APONTAMENTO',
    canal: 'WHATSAPP',
    titulo: 'Cobrança SME',
    mensagem:
      '*TEG/SME — Cobrança* DRE *{dre_codigo} - {dre_nome}*: apontamentos da competência *{competencia}* estão pendentes ({qtd_lotes_pendentes} lote(s)). {mensagem_custom} ⏰ *Prazo de resposta: {prazo_resposta}* ({sla_dias_uteis} dias úteis).',
    ativo: true,
    atualizadoEm: '2026-05-11',
  },
  {
    id: 'fechamento-devolvido-dre-email',
    evento: 'FECHAMENTO_DEVOLVIDO_DRE',
    canal: 'EMAIL',
    titulo: 'Fechamento {{mes_referencia}} devolvido para correção',
    mensagem:
      'O fechamento da competência {{mes_referencia}} foi devolvido pela SME. Motivo: {{motivo}}',
    ativo: true,
    atualizadoEm: '2026-05-07',
  },
  {
    id: 'fechamento-devolvido-dre-inapp',
    evento: 'FECHAMENTO_DEVOLVIDO_DRE',
    canal: 'IN_APP',
    titulo: 'Fechamento {{mes_referencia}} devolvido',
    mensagem: 'Motivo: {{motivo}}. Acesse o fechamento para realizar os ajustes.',
    ativo: true,
    atualizadoEm: '2026-05-07',
  },
  {
    id: 'fechamento-disponibilizado-dre-email',
    evento: 'FECHAMENTO_DISPONIBILIZADO_DRE',
    canal: 'EMAIL',
    titulo: 'Fechamento mensal {{mes_referencia}} disponível',
    mensagem:
      'O fechamento mensal da competência {{mes_referencia}} foi gerado e está disponível para validação no portal SME/DRE.',
    ativo: true,
    atualizadoEm: '2026-05-07',
  },
  {
    id: 'fechamento-disponibilizado-dre-inapp',
    evento: 'FECHAMENTO_DISPONIBILIZADO_DRE',
    canal: 'IN_APP',
    titulo: 'Fechamento {{mes_referencia}} disponível',
    mensagem:
      'Acesse /sme/fechamento-mensal para validar o fechamento da competência {{mes_referencia}}.',
    ativo: true,
    atualizadoEm: '2026-05-07',
  },
  {
    id: 'fechamento-selado-email',
    evento: 'FECHAMENTO_SELADO',
    canal: 'EMAIL',
    titulo: 'Fechamento {{mes_referencia}} selado',
    mensagem:
      'O fechamento financeiro de {{mes_referencia}} foi selado pelo DTP. Demonstrativos liberados aos credenciados.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'fechamento-selado-inapp',
    evento: 'FECHAMENTO_SELADO',
    canal: 'IN_APP',
    titulo: 'Fechamento selado — {{mes_referencia}}',
    mensagem: 'Demonstrativos disponíveis no Portal do Credenciado.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'inscricao-aguardando-correcao-todos',
    evento: 'INSCRICAO_AGUARDANDO_CORRECAO',
    canal: 'TODOS',
    titulo: 'Correção solicitada',
    mensagem:
      'TEG: a DTP solicitou ajustes na inscrição {{protocolo}}. Verifique o link de acompanhamento.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-aprovada-todos',
    evento: 'INSCRICAO_APROVADA',
    canal: 'TODOS',
    titulo: 'Inscrição aprovada',
    mensagem:
      'TEG: sua inscrição {{protocolo}} foi APROVADA. Acesse o Portal do Credenciado para próximos passos.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-em-analise-todos',
    evento: 'INSCRICAO_EM_ANALISE',
    canal: 'TODOS',
    titulo: 'Inscrição em análise',
    mensagem: 'TEG: sua inscrição {{protocolo}} entrou em análise pela DTP.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-expirada-todos',
    evento: 'INSCRICAO_EXPIRADA',
    canal: 'TODOS',
    titulo: 'Inscrição expirada',
    mensagem: 'TEG: a inscrição {{protocolo}} foi expirada por falta de resposta no prazo.',
    ativo: true,
    atualizadoEm: '2026-09-06',
  },
  {
    id: 'inscricao-nova-mensagem-todos',
    evento: 'INSCRICAO_NOVA_MENSAGEM',
    canal: 'TODOS',
    titulo: 'Nova mensagem da DTP',
    mensagem:
      'TEG: você tem uma nova mensagem na inscrição {{protocolo}}. Acesse o link de acompanhamento.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-pendente-docs-todos',
    evento: 'INSCRICAO_PENDENTE_DOCS',
    canal: 'TODOS',
    titulo: 'Documentos pendentes',
    mensagem:
      'TEG: sua inscrição {{protocolo}} tem documentos pendentes. Reenvie até {{prazo}} pelo link de acompanhamento.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-prazo-proximo-todos',
    evento: 'INSCRICAO_PRAZO_PROXIMO',
    canal: 'TODOS',
    titulo: 'Prazo próximo do fim',
    mensagem:
      'TEG: o prazo para resolver pendências da inscrição {{protocolo}} vence em {{prazo}}.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-recebida-todos',
    evento: 'INSCRICAO_RECEBIDA',
    canal: 'TODOS',
    titulo: 'Inscrição recebida',
    mensagem:
      'TEG: recebemos sua inscrição (protocolo {{protocolo}}). Acompanhe pelo link enviado.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-reenviada-todos',
    evento: 'INSCRICAO_REENVIADA',
    canal: 'TODOS',
    titulo: 'Reenvio confirmado',
    mensagem: 'TEG: recebemos o reenvio da inscrição {{protocolo}}. Voltará para análise.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'inscricao-reprovada-todos',
    evento: 'INSCRICAO_REPROVADA',
    canal: 'TODOS',
    titulo: 'Inscrição reprovada',
    mensagem: 'TEG: sua inscrição {{protocolo}} foi reprovada. Motivo: {{motivo}}.',
    ativo: true,
    atualizadoEm: '2026-04-28',
  },
  {
    id: 'medicao-em-divergencia-dtp-email',
    evento: 'MEDICAO_EM_DIVERGENCIA_DTP',
    canal: 'EMAIL',
    titulo: 'Divergência apontada pelo DTP — {{mes_referencia}}',
    mensagem:
      'O DTP identificou divergência na medição de {{mes_referencia}} (DRE {{dre_codigo}}) e devolveu à SME para reanálise. Motivo: {{motivo}}.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'medicao-em-divergencia-dtp-inapp',
    evento: 'MEDICAO_EM_DIVERGENCIA_DTP',
    canal: 'IN_APP',
    titulo: 'Divergência DTP — {{mes_referencia}}',
    mensagem: 'SME deve reanalisar. Motivo: {{motivo}}.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'medicao-recebida-dtp-email',
    evento: 'MEDICAO_RECEBIDA_DTP',
    canal: 'EMAIL',
    titulo: 'Medição recebida pelo DTP — {{mes_referencia}}',
    mensagem:
      'O DTP recebeu a medição de {{mes_referencia}} (DRE {{dre_codigo}}) e iniciou a etapa de fechamento financeiro.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
  {
    id: 'medicao-recebida-dtp-inapp',
    evento: 'MEDICAO_RECEBIDA_DTP',
    canal: 'IN_APP',
    titulo: 'DTP recebeu a medição',
    mensagem: '{{mes_referencia}} — DRE {{dre_codigo}}.',
    ativo: true,
    atualizadoEm: '2026-05-21',
  },
];
